/**
 * Listenzustand in der Adresse (Regel „Dokument-Navigation" R2): Filter, Suche,
 * Sortierung, Reiter, Ansicht und Datum stehen als Parameter in der Adresse der Liste.
 * Nur so stellt „Zurück" sie wieder her. Reine Funktionen — der Hook dazu liegt in
 * useListState.ts. Vorbild: bauberichte ReportsPage.
 *
 * Standardwerte stehen nicht in der Adresse; fremde Parameter bleiben unberührt.
 */
export interface ParamDef<T> {
  default: T;
  /** undefined = ungültig → Standardwert */
  parse(raw: string): T | undefined;
  format(v: T): string;
}

/** Methoden-Schreibweise in ParamDef macht ParamDef<string> zu ParamDef<unknown> zuweisbar. */
export type ListSchema = Record<string, ParamDef<unknown>>;
export type ListValues<S> = { [K in keyof S]: S[K] extends ParamDef<infer T> ? T : never };

export interface SortValue<K extends string> { key: K; dir: 'asc' | 'desc' }

export const param = {
  string(def = ''): ParamDef<string> {
    return { default: def, parse: (r) => r, format: (v) => v };
  },
  bool(def = false): ParamDef<boolean> {
    return { default: def, parse: (r) => (r === '1' || r === 'true' ? true : r === '0' || r === 'false' ? false : undefined), format: (v) => (v ? '1' : '0') };
  },
  number(def: number): ParamDef<number> {
    return { default: def, parse: (r) => (/^-?\d+$/.test(r) ? parseInt(r, 10) : undefined), format: (v) => String(v) };
  },
  /** JJJJ-MM-TT */
  date(def = ''): ParamDef<string> {
    return { default: def, parse: (r) => (/^\d{4}-\d{2}-\d{2}$/.test(r) ? r : undefined), format: (v) => v };
  },
  /** JJJJ-MM */
  month(def = ''): ParamDef<string> {
    return { default: def, parse: (r) => (/^\d{4}-\d{2}$/.test(r) ? r : undefined), format: (v) => v };
  },
  oneOf<T extends string>(values: readonly T[], def: T): ParamDef<T> {
    return { default: def, parse: (r) => ((values as readonly string[]).includes(r) ? (r as T) : undefined), format: (v) => v };
  },
  /** „titel:asc" */
  sort<K extends string>(keys: readonly K[], def: SortValue<K>): ParamDef<SortValue<K>> {
    return {
      default: def,
      parse: (r) => {
        const [k, d] = r.split(':');
        if (!k || !(keys as readonly string[]).includes(k)) return undefined;
        return { key: k as K, dir: d === 'desc' ? 'desc' : 'asc' };
      },
      format: (v) => `${v.key}:${v.dir}`,
    };
  },
};

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

export function readListState<S extends ListSchema>(schema: S, sp: URLSearchParams): ListValues<S> {
  const out: Record<string, unknown> = {};
  for (const name of Object.keys(schema)) {
    const def = schema[name]!;
    const raw = sp.get(name);
    const parsed = raw == null ? undefined : def.parse(raw);
    out[name] = parsed === undefined ? def.default : parsed;
  }
  return out as ListValues<S>;
}

/** Liefert neue Parameter; Standardwerte werden entfernt, fremde Parameter bleiben. */
export function writeListState<S extends ListSchema>(schema: S, sp: URLSearchParams, patch: Partial<ListValues<S>>): URLSearchParams {
  const next = new URLSearchParams(sp);
  for (const name of Object.keys(patch)) {
    const def = schema[name];
    if (!def) continue;
    const value = (patch as Record<string, unknown>)[name];
    if (value === undefined || sameValue(value, def.default)) next.delete(name);
    else next.set(name, def.format(value));
  }
  return next;
}

export function isDefaultState<S extends ListSchema>(schema: S, values: ListValues<S>): boolean {
  return Object.keys(schema).every((name) => sameValue((values as Record<string, unknown>)[name], schema[name]!.default));
}
